/**
 * Orders API Service
 * Handles all order-related API calls
 */

import API_BASE_URL from './apiConfig';
import { getAuthHeaders } from './authApi';
import { formatDate } from '../utils/dateUtils';

/**
 * Transform backend order data to frontend format
 */
const transformOrder = (order) => {
  return {
    id: order._id,
    sn: 0, // Will be set based on index
    clientName: order.client_name || '',
    location: order.city || order.full_address?.city || '',
    orderDate: formatDate(order.createdAt),
    orderStatus: order.current_status || '',
    orderValue: order.quotations?.quotation_amt || 0,
    quotationNumber: order.quotation_number_main || order.quotations?.quotation_number || '',
    // Additional fields
    quotation_number_main: order.quotation_number_main || '',
    client_id: order.client_id?._id || order.client_id,
    enquiry_id: order.enquiry_id?._id || order.enquiry_id,
  };
};

/**
 * Transform frontend order data to backend format
 */
const transformOrderForBackend = (order) => {
  return {
    quotation_number_main: order.quotation_number_main,
    client_name: order.client_name || order.clientName,
    client_id: order.client_id,
    client_email: order.client_email,
    client_mobile: order.client_mobile,
    contact_person_name: order.contact_person_name,
    contact_person_designation: order.contact_person_designation,
    full_address: order.full_address,
    city: order.city || order.location,
    state: order.state,
    enquiry_id: order.enquiry_id,
    enquiry_number: order.enquiry_number,
    medium: order.medium,
    source: order.source,
    source_comment: order.source_comment,
    current_status: order.current_status || order.orderStatus,
    is_order_received: order.is_order_received,
    quotations: order.quotations,
  };
};

/**
 * List orders with filters, sorting, and pagination
 */
export const listOrders = async (filters = {}, sort = {}, page = 0, rowsPerPage = 10) => {
  try {
    const params = new URLSearchParams({
      page: (page + 1).toString(),
      limit: rowsPerPage.toString(),
    });

    if (filters.client_name || filters.clientName) {
      params.append('client_name', filters.client_name || filters.clientName);
    }
    if (filters.status) {
      params.append('status', filters.status);
    }
    if (filters.quotation_number || filters.quotationNumber) {
      params.append('quotation_number', filters.quotation_number || filters.quotationNumber);
    }

    const response = await fetch(`${API_BASE_URL}/order?${params.toString()}`);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: `HTTP error! status: ${response.status}` }));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    const orders = result.data || [];
    const transformed = orders.map((order, index) => ({
      ...transformOrder(order),
      sn: page * rowsPerPage + index + 1,
    }));

    return {
      data: transformed,
      total: result.total || 0,
      page,
      rowsPerPage,
    };
  } catch (error) {
    console.error('Error fetching orders:', error);
    throw error;
  }
};

/**
 * Get single order by ID
 */
export const getOrderById = async (id) => {
  try {
    const response = await fetch(`${API_BASE_URL}/order/${id}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: `HTTP error! status: ${response.status}` }));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    const order = await response.json();
    return transformOrder(order);
  } catch (error) {
    console.error('Error fetching order:', error);
    throw error;
  }
};

/**
 * Create new order
 */
export const createOrder = async (data) => {
  try {
    const payload = transformOrderForBackend({
      ...data,
      clientName: data.clientName,
      location: data.location,
      quotation_number_main: data.quotationNumber,
      current_status: data.orderStatus,
      quotations: {
        quotation_amt: parseFloat(data.orderValue) || 0,
        quotation_number: data.quotationNumber,
      },
    });
    
    const response = await fetch(`${API_BASE_URL}/order`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: `HTTP error! status: ${response.status}` }));
      throw new Error(errorData.message || 'Failed to create order');
    }

    const result = await response.json();

    return {
      success: true,
      message: 'Order created successfully',
      data: transformOrder(result),
    };
  } catch (error) {
    console.error('Error creating order:', error);
    return {
      success: false,
      error: error.message || 'Failed to create order',
    };
  }
};

/**
 * Update order
 */
export const updateOrder = async (id, updates) => {
  try {
    const payload = transformOrderForBackend(updates);
    
    const response = await fetch(`${API_BASE_URL}/order/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: `HTTP error! status: ${response.status}` }));
      throw new Error(errorData.message || 'Failed to update order');
    }

    const result = await response.json();

    return {
      success: true,
      data: transformOrder(result),
    };
  } catch (error) {
    console.error('Error updating order:', error);
    return {
      success: false,
      error: error.message || 'Failed to update order',
    };
  }
};

/**
 * Delete order
 */
export const deleteOrder = async (id) => {
  try {
    const response = await fetch(`${API_BASE_URL}/order/${id}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
    });

    if (!response.ok) {
      throw new Error('Failed to delete order');
    }

    return {
      success: true,
    };
  } catch (error) {
    console.error('Error deleting order:', error);
    return {
      success: false,
      error: error.message || 'Failed to delete order',
    };
  }
};

/**
 * Get order count by employee (Admin only)
 */
export const getEmployeeOrderCounts = async (options = {}) => {
  // const { skipCache = false, cacheUserId } = options;
  try {
    const response = await fetch(`${API_BASE_URL}/orders/employee-counts`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Failed to fetch employee counts');
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching employee order counts:', error);
    return [];
  }
};

/**
 * Get unique cities and states for orders filters
 */
export const getFilterOptions = async (options = {}) => {
  // const { skipCache = false, cacheUserId } = options;
  try {
    const response = await fetch(`${API_BASE_URL}/orders/filter-options`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Failed to fetch filter options');
    }

    const data = await response.json();
    const payload = { cities: data.cities || [], states: data.states || [] };
    return payload;
  } catch (error) {
    console.error('Error fetching filter options:', error);
    return { cities: [], states: [] };
  }
};

// Export as default object for backward compatibility
const ordersApi = {
  list: listOrders,
  get: getOrderById,
  create: createOrder,
  update: updateOrder,
  delete: deleteOrder,
  getEmployeeOrderCounts,
  getFilterOptions,
};

export default ordersApi;
